import type { ReactNode } from "react";

import { Link } from "react-router-dom";

import type { LucideIcon } from "lucide-react";
import { motion } from "motion/react";

import { cn } from "@/lib/utils";

export type StatTone = "neutral" | "coral" | "mint" | "amber" | "sky" | "violet";

/** Web ilovadagi plitalar bilan bir xil palitra — ikki ilova bir oila. */
export const TONES: Record<StatTone, { card: string; icon: string; glow: string; accent: string }> =
  {
    neutral: {
      card: "bg-card",
      icon: "bg-muted text-muted-foreground",
      glow: "bg-muted-foreground",
      accent: "text-foreground",
    },
    coral: {
      card: "bg-tile-coral",
      icon: "bg-tile-coral-strong text-card",
      glow: "bg-tile-coral-strong",
      accent: "text-tile-coral-strong",
    },
    mint: {
      card: "bg-tile-mint",
      icon: "bg-tile-mint-strong text-card",
      glow: "bg-tile-mint-strong",
      accent: "text-tile-mint-strong",
    },
    amber: {
      card: "bg-tile-amber",
      icon: "bg-tile-amber-strong text-card",
      glow: "bg-tile-amber-strong",
      accent: "text-tile-amber-strong",
    },
    sky: {
      card: "bg-tile-sky",
      icon: "bg-tile-sky-strong text-card",
      glow: "bg-tile-sky-strong",
      accent: "text-tile-sky-strong",
    },
    violet: {
      card: "bg-tile-violet",
      icon: "bg-tile-violet-strong text-card",
      glow: "bg-tile-violet-strong",
      accent: "text-tile-violet-strong",
    },
  };

interface StatTileProps {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: StatTone;
  /** O'ng yuqori burchakdagi qo'shimcha (masalan, foiz doirasi). */
  aside?: ReactNode;
  /** Plita bosilsa ochiladigan ro'yxat (masalan, shu holatdagi foydalanuvchilar). */
  to?: string;
  /** Ketma-ket paydo bo'lish tartibi. */
  index?: number;
  className?: string;
}

export function StatTile({
  icon: Icon,
  label,
  value,
  hint,
  tone = "neutral",
  aside,
  to,
  index = 0,
  className,
}: StatTileProps) {
  const palette = TONES[tone];
  const body = (
    <>
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute -top-10 -right-10 size-28 rounded-full opacity-15 blur-2xl transition-transform duration-500 group-hover:scale-150",
          palette.glow,
        )}
      />
      <div className="relative flex items-start justify-between gap-2">
        <span
          className={cn(
            "flex size-9 items-center justify-center rounded-xl shadow-sm transition-transform duration-300 group-hover:-rotate-6",
            palette.icon,
          )}
        >
          <Icon className="size-[18px]" aria-hidden />
        </span>
        {aside ? <div className={palette.accent}>{aside}</div> : null}
      </div>
      <div className="text-foreground/70 relative mt-3 text-sm font-medium">{label}</div>
      <div className="relative mt-0.5 text-3xl font-semibold tracking-tight tabular-nums">
        {value}
      </div>
      {hint ? (
        <div className="text-foreground/60 relative mt-1 truncate text-xs">{hint}</div>
      ) : null}
    </>
  );
  const classes = cn(
    "group ring-foreground/5 hover:shadow-foreground/5 relative block h-full overflow-hidden rounded-2xl p-4 ring-1 transition-shadow hover:shadow-lg",
    to && "focus-visible:ring-ring outline-none focus-visible:ring-2",
    palette.card,
    className,
  );
  return (
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, delay: index * 0.06, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -3 }}
    >
      {to ? (
        <Link to={to} className={classes}>
          {body}
        </Link>
      ) : (
        <div className={classes}>{body}</div>
      )}
    </motion.div>
  );
}
